import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Loader2, Stamp, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, unauthenticated read-only verification page for a shared
 * OneStamp. The owner can revoke the link at any time and this page
 * will start returning "revoked" instead.
 */
export default function OneStampPublicVerifyPage() {
  const { code } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, err: "" });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/onestamp/public/${code}`);
        setState({ status: "ok", data: r.data, err: "" });
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (e?.response?.status === 404) {
          setState({ status: "revoked", data: null, err: detail });
        } else {
          setState({ status: "error", data: null,
                     err: detail || e?.message || "Unable to verify." });
        }
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 font-mono flex items-center justify-center p-6">
      <div className="max-w-2xl w-full border border-[#27272A] bg-[#0B0B0B] p-8" data-testid="onestamp-public-panel">
        <div className="flex items-center gap-2 mb-4">
          <Stamp className="text-amber-400" size={20} />
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">
            ONESTAMP · PUBLIC_VERIFICATION
          </div>
        </div>

        {state.status === "loading" && (
          <div className="flex items-center gap-2 text-zinc-400 text-[12px]">
            <Loader2 className="animate-spin" size={16}/> Verifying signature…
          </div>
        )}

        {state.status === "revoked" && (
          <div className="border border-rose-500/40 bg-rose-500/10 p-5 text-rose-200 flex items-start gap-3"
               data-testid="onestamp-public-revoked">
            <ShieldAlert className="shrink-0 mt-0.5" size={22} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em]">LINK_REVOKED_OR_INVALID</div>
              <p className="text-[12px] mt-1">
                This verification link is no longer active. The stamp owner may have revoked
                it, rotated to a new code, or deleted the original stamp entirely. If you
                need the underlying proof, ask the stamp owner for a fresh link or the
                signed PDF certificate.
              </p>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div className="border border-rose-500/40 bg-rose-500/10 p-5 text-rose-200 text-[12px]"
               data-testid="onestamp-public-error">
            {state.err}
          </div>
        )}

        {state.status === "ok" && state.data && (
          <div>
            <div className={`p-4 border flex items-start gap-3 ${
              state.data.valid
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border-rose-500/40 bg-rose-500/10 text-rose-200"}`}
                 data-testid="onestamp-public-result">
              {state.data.valid
                ? <ShieldCheck className="shrink-0 mt-0.5" size={22} />
                : <ShieldAlert className="shrink-0 mt-0.5" size={22} />}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em]">
                  {state.data.valid ? "VERIFIED · PROOF_INTACT" : "VERIFICATION_FAILED"}
                </div>
                <div className="text-[12px] mt-1">
                  hash_matches=<b>{String(state.data.hash_matches)}</b> ·
                  signature_valid=<b>{String(state.data.signature_valid)}</b>
                </div>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-[130px_1fr] gap-y-2 text-[11px]">
              <dt className="text-zinc-500">stamp_id</dt>
              <dd className="text-zinc-200 break-all">{state.data.stamp_id}</dd>
              <dt className="text-zinc-500">signed_at (UTC)</dt>
              <dd className="text-zinc-200">{state.data.signed_at}</dd>
              <dt className="text-zinc-500">shared_at (UTC)</dt>
              <dd className="text-zinc-200">{state.data.shared_at}</dd>
              <dt className="text-zinc-500">algorithm</dt>
              <dd className="text-zinc-200">{state.data.algo} · v{state.data.version}</dd>
              <dt className="text-zinc-500">preview</dt>
              <dd className="text-zinc-300">{state.data.preview || "(no preview)"}</dd>
            </dl>

            <div className="mt-5">
              <div className="text-[10px] text-zinc-500 mb-1">SHA-256 CONTENT HASH</div>
              <div className="border border-[#27272A] bg-black/40 p-2 text-[10px] font-mono break-all">
                {state.data.content_hash}
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] text-zinc-500 mb-1">HMAC-SHA256 SIGNATURE</div>
              <div className="border border-[#27272A] bg-black/40 p-2 text-[10px] font-mono break-all">
                {state.data.signature}
              </div>
            </div>

            <div className="mt-6 text-[10px] text-zinc-500 leading-relaxed">
              {state.data.disclaimer}
            </div>
          </div>
        )}

        <div className="mt-6 border-t border-[#27272A] pt-4 text-[11px] text-zinc-500 flex items-center gap-2">
          <Link to="/" className="hover:text-white flex items-center gap-1">
            <ExternalLink size={11}/> LEARN_MORE_ABOUT_ONESTAMP
          </Link>
        </div>
      </div>
    </div>
  );
}
